import React from 'react';
import styled from '@emotion/styled';
import { Box, Typography, Chip } from '@mui/material';
import { formatPayloadDate } from '../utils/suggestionUtils';

const Fields = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
}));

const Field = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
});

const FieldLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.25),
}));

const FieldValue = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
}));

const BodyBlock = styled(Box)(({ theme }) => ({
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: theme.typography.size.md,
    color: theme.palette.text.primary,
    padding: theme.spacing(1.25),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    background: theme.palette.action.hover,
}));

const CardList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
}));

const CardItem = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
}));

const CardName = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    fontWeight: theme.typography.fontWeightMedium,
}));

const CardDescription = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    whiteSpace: 'pre-wrap',
}));

const ChipRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
}));

const RawJson = styled('pre')(({ theme }) => ({
    margin: 0,
    padding: theme.spacing(1.25),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.action.hover,
    fontSize: theme.typography.size.sm,
    overflowX: 'auto',
}));

const EmptyNote = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
}));

const PRIVACY_COLOR = {
    Public: 'info',
    Internal: 'warning',
    Private: 'error',
};

const POST_ON_LABEL = {
    project: 'The project',
    task: 'The task',
};

const PayloadField = ({ label, children }) => (
    <Field>
        <FieldLabel>{label}</FieldLabel>
        {children}
    </Field>
);

const PlainField = ({ label, value }) => (
    <PayloadField label={label}>
        <FieldValue>{value}</FieldValue>
    </PayloadField>
);

// `refs` are resolved server-side; an id we could not resolve is named as missing rather
// than shown raw, because an ObjectId tells a reviewer nothing about what they are approving.
const RefList = ({ label, refs }) => (
    <PayloadField label={label}>
        <ChipRow>
            {refs.map((ref, i) => (
                <Chip
                    key={`${ref.id || 'ref'}-${i}`}
                    label={ref.exists && ref.name ? ref.name : 'No longer exists'}
                    size="small"
                    variant="outlined"
                    color={ref.exists && ref.name ? 'default' : 'warning'}
                />
            ))}
        </ChipRow>
    </PayloadField>
);

const RawFallback = ({ payload }) => (
    <PayloadField label="Details">
        <RawJson>{JSON.stringify(payload, null, 2)}</RawJson>
    </PayloadField>
);

// Anything we could not put into words falls back to the literal payload — a blank
// "What will happen" next to an enabled Approve button would be the worst outcome here.
const FieldSet = ({ fields, payload }) => {
    const rendered = (fields || []).filter(Boolean);
    return <Fields>{rendered.length > 0 ? rendered : <RawFallback payload={payload} />}</Fields>;
};

const refName = (ref) => (ref && ref.exists && ref.name ? ref.name : null);

/**
 * Renders the literal `payload` of a suggested action in plain language so a
 * reviewer can see exactly what will happen before approving. Falls back to raw
 * JSON for shapes we do not have a purpose-built view for.
 */
const SuggestionPayload = ({ actionType, payload, payloadRefs }) => {
    if (!payload || typeof payload !== 'object') {
        return <EmptyNote>This suggestion carries no content to preview.</EmptyNote>;
    }

    const refs = payloadRefs || {};

    switch (actionType) {
        case 'add_comment':
            return (
                <FieldSet
                    payload={payload}
                    fields={[
                        payload.privacy && (
                            <PayloadField key="privacy" label="Visibility">
                                <ChipRow>
                                    <Chip label={payload.privacy} size="small" color={PRIVACY_COLOR[payload.privacy] || 'default'} />
                                    {payload.privacy === 'Public' && (
                                        <Typography variant="caption" color="text.secondary">
                                            The client will be able to see this comment.
                                        </Typography>
                                    )}
                                </ChipRow>
                            </PayloadField>
                        ),
                        payload.postOn && <PlainField key="postOn" label="Post on" value={POST_ON_LABEL[payload.postOn] || payload.postOn} />,
                        payload.commentBody && (
                            <PayloadField key="body" label="Comment that will be posted">
                                <BodyBlock>{payload.commentBody}</BodyBlock>
                            </PayloadField>
                        ),
                    ]}
                />
            );

        case 'send_reminder':
            return (
                <FieldSet
                    payload={payload}
                    fields={[
                        refName(refs.reminderUser) && <PlainField key="to" label="Reminder goes to" value={refName(refs.reminderUser)} />,
                        payload.reminderBody && (
                            <PayloadField key="body" label="Reminder that will be sent">
                                <BodyBlock>{payload.reminderBody}</BodyBlock>
                            </PayloadField>
                        ),
                    ]}
                />
            );

        case 'create_task':
            return (
                <FieldSet
                    payload={payload}
                    fields={[
                        payload.name && <PlainField key="name" label="Task name" value={payload.name} />,
                        payload.overview && (
                            <PayloadField key="overview" label="Overview">
                                <BodyBlock>{payload.overview}</BodyBlock>
                            </PayloadField>
                        ),
                        payload.dueDate && <PlainField key="due" label="Due date" value={formatPayloadDate(payload.dueDate)} />,
                        payload.priority && <PlainField key="priority" label="Priority" value={payload.priority} />,
                        <PlainField key="skill" label="Skill" value={refName(refs.skill) || "The project's skill"} />,
                        Array.isArray(refs.assignees) && refs.assignees.length > 0 && <RefList key="assignees" label="Assignees" refs={refs.assignees} />,
                    ]}
                />
            );

        case 'create_project':
            return (
                <FieldSet
                    payload={payload}
                    fields={[
                        payload.name && <PlainField key="name" label="Project name" value={payload.name} />,
                        payload.overview && (
                            <PayloadField key="overview" label="Overview">
                                <BodyBlock>{payload.overview}</BodyBlock>
                            </PayloadField>
                        ),
                        payload.priority && <PlainField key="priority" label="Priority" value={payload.priority} />,
                    ]}
                />
            );

        case 'complete_task':
            return (
                <Fields>
                    {payload.reason ? (
                        <PayloadField label="Why it should be completed">
                            <BodyBlock>{payload.reason}</BodyBlock>
                        </PayloadField>
                    ) : (
                        <EmptyNote>The task will be marked complete. No reason was given.</EmptyNote>
                    )}
                </Fields>
            );

        case 'assign_responsible': {
            // Same rule as RefList: an id we cannot put a name to is named as missing, never shown raw.
            const responsibleName = refName(refs.user) || payload.userName || null;
            return (
                <FieldSet
                    payload={payload}
                    fields={[(responsibleName || payload.userId) && <PlainField key="user" label="New responsible person" value={responsibleName || 'No longer exists'} />]}
                />
            );
        }

        case 'update_assignees': {
            if (!Array.isArray(payload.assignees)) {
                return <FieldSet payload={payload} fields={[]} />;
            }
            if (payload.assignees.length === 0) {
                return (
                    <Fields>
                        <PayloadField label="Assignees will be set to">
                            <EmptyNote>Everyone will be removed from this item.</EmptyNote>
                        </PayloadField>
                    </Fields>
                );
            }
            return (
                <FieldSet
                    payload={payload}
                    fields={[Array.isArray(refs.assignees) && <RefList key="assignees" label="Assignees will be set to" refs={refs.assignees} />]}
                />
            );
        }

        case 'change_status':
            return <FieldSet payload={payload} fields={[payload.status && <PlainField key="status" label="Status will change to" value={payload.status} />]} />;

        case 'change_priority':
            return (
                <FieldSet payload={payload} fields={[payload.priority && <PlainField key="priority" label="Priority will change to" value={payload.priority} />]} />
            );

        case 'change_due_date':
            return (
                <FieldSet
                    payload={payload}
                    fields={[payload.dueDate && <PlainField key="due" label="Due date will change to" value={formatPayloadDate(payload.dueDate)} />]}
                />
            );

        case 'add_blocker':
            return (
                <FieldSet
                    payload={payload}
                    fields={[
                        Array.isArray(refs.blockedBy) && refs.blockedBy.length > 0 && <RefList key="blockedBy" label="Blocked by" refs={refs.blockedBy} />,
                        payload.reason && (
                            <PayloadField key="reason" label="Why it is blocked">
                                <BodyBlock>{payload.reason}</BodyBlock>
                            </PayloadField>
                        ),
                    ]}
                />
            );

        case 'create_qa_card': {
            const cards = Array.isArray(payload.cards) ? payload.cards : [];
            if (cards.length === 0) return <RawFallback payload={payload} />;
            return (
                <Fields>
                    <PayloadField label={`QA cards that will be added (${cards.length})`}>
                        <CardList>
                            {cards.map((card, i) => (
                                <CardItem key={`${card?.name || 'card'}-${i}`}>
                                    <CardName>{card?.name || 'Untitled card'}</CardName>
                                    {card?.description && <CardDescription>{card.description}</CardDescription>}
                                </CardItem>
                            ))}
                        </CardList>
                    </PayloadField>
                </Fields>
            );
        }

        default:
            return (
                <Fields>
                    <RawFallback payload={payload} />
                </Fields>
            );
    }
};

export default SuggestionPayload;
